import React, { useEffect, useState, useRef } from 'react';
import { ReactComponent as Kakao } from 'src/assets/img/Oauth/kakaoIcon.svg';
import { ReactComponent as Naver } from 'src/assets/img/Oauth/naverIcon.svg';
import { ReactComponent as Google } from 'src/assets/img/Oauth/googleIcon.svg';
import { ReactComponent as Close } from 'src/assets/close.svg';
import { useNavigate } from 'react-router-dom';
import { clearStoredAuth, fetchAndSaveUserSession } from '../../auth/userSessionStorage';

interface SignupModalProps {
    onClose: () => void;
    setNickName: (nickName: string) => void;
    token: string;
    setToken: (token: string) => void;
    closeModal: () => void;
}

const LoginModal: React.FC<SignupModalProps> = ({ onClose, setNickName, token, setToken, closeModal }) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [messageHandled, setMessageHandled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.origin === window.location.origin && !messageHandled) {
                const { data } = event;
                if (data && data.status && data.platform) {
                    if (data.status === 'success') {
                        try {
                            const session = await fetchAndSaveUserSession(data.token);
                            setToken(session.jwtToken || '');
                            setNickName(session.nickName || '사용자');
                            setMessageHandled(true);
                            onClose();
                            window.dispatchEvent(new Event('login_success'));
                            navigate('/');
                        } catch (error) {
                            clearStoredAuth();
                            console.error('Failed to load user session after login:', error);
                        }
                    }
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [messageHandled, setNickName, setToken, onClose]);

    const deleteNaverCookies = () => {
        const cookies = document.cookie.split('; ');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.naver.com`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
        }
    };

    const openPopup = (url: string, platform: string) => {
        const width = 600;
        const height = 600;
        const left = (window.innerWidth / 2) - (width / 2);
        const top = (window.innerHeight / 2) - (height / 2);
        window.open(url, `${platform} Login`, `width=${width},height=${height},top=${top},left=${left}`);
    };

    const handleKakaoLogin = () => {
        const CLIENT_ID = process.env.REACT_APP_KAKAO_CLIENT_ID;
        const host = window.location.origin;
        const REDIRECT_URI = `${host}/api/sign/oauth2/callback/kakao`;
        const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code`;
        openPopup(kakaoAuthUrl, 'kakao');
    };

    const handleNaverLogin = () => {
        deleteNaverCookies();
        const CLIENT_ID = process.env.REACT_APP_NAVER_CLIENT_ID;
        const host = window.location.origin;
        const REDIRECT_URI = `${host}/api/sign/oauth2/callback/naver`;
        const state = `FORCE_LOGIN_${new Date().getTime()}`;
        const naverAuthUrl = `https://nid.naver.com/oauth2.0/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&state=${state}&prompt=login`;
        openPopup(naverAuthUrl, 'naver');
    };

    const handleGoogleLogin = () => {
        const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const host = window.location.origin;
        const REDIRECT_URI = `${host}/api/sign/oauth2/callback/google`;
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=email%20profile`;
        openPopup(googleAuthUrl, 'google');
    };

    return (
        <>
            <div className="overlay" />
            <div ref={modalRef} className="penpal-alert">
                <div className="flex flex-col p-7 gap-6">
                    {/* 헤더 */}
                    <div className="flex items-center justify-between">
                        <h2 className="text-[20px] font-bold text-[#222]">간편 로그인</h2>
                        <button
                            onClick={closeModal}
                            className="w-8 h-8 flex justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <Close className="w-4 h-4 text-gray-400" />
                        </button>
                    </div>

                    {/* 구분선 */}
                    <div className="h-px bg-[#f0f0f0]" />

                    {/* 로그인 버튼들 */}
                    <div className="flex flex-col gap-3">
                        <button
                            className="w-full h-[52px] flex items-center justify-center gap-2.5 bg-[#FEE500] hover:bg-[#f0d900] rounded-xl transition-colors"
                            onClick={handleKakaoLogin}
                        >
                            <Kakao className="w-5 h-5 flex-shrink-0" />
                            <span className="text-[15px] font-semibold text-[#3C1E1E]">카카오 로그인</span>
                        </button>

                        <button
                            className="w-full h-[52px] flex items-center justify-center gap-2.5 bg-[#03C75A] hover:bg-[#02b350] rounded-xl transition-colors"
                            onClick={handleNaverLogin}
                        >
                            <Naver className="w-5 h-5 flex-shrink-0" />
                            <span className="text-[15px] font-semibold text-white">네이버 로그인</span>
                        </button>

                        <button
                            className="w-full h-[52px] flex items-center justify-center gap-2.5 bg-white border border-[#e0e0e0] hover:bg-gray-50 rounded-xl transition-colors"
                            onClick={handleGoogleLogin}
                        >
                            <Google className="w-5 h-5 flex-shrink-0" />
                            <span className="text-[15px] font-semibold text-[#444]">구글 로그인</span>
                        </button>
                    </div>

                    <p className="text-[12px] text-[#bbb] text-center leading-relaxed">
                        로그인 시 서비스 이용약관 및<br />개인정보처리방침에 동의하는 것으로 간주됩니다.
                    </p>
                </div>
            </div>
        </>
    );
};

export default LoginModal;
